import { usePermissionStore } from '@/stores/permissions';
import { useUserStore } from '@/stores/user';
import { computed } from 'vue';

/**
 * Permission Management Composable - Store Based
 * Centralized permission management using Pinia store
 * 
 * This composable provides a clean interface to the permission store,
 * maintaining backward compatibility while leveraging the new store architecture.
 * 
 * @example Basic Usage
 * ```vue
 * <script setup>
 * import { usePermissions } from '@/composables/usePermissions';
 * 
 * const { can, module, userIsAdmin } = usePermissions();
 * const calculator = module('CALCULATOR');
 * </script>
 * 
 * <template>
 *   <button v-if="calculator.canWrite.value">Save</button>
 *   <div v-if="userIsAdmin">Admin Panel</div>
 * </template>
 * ```
 */
export function usePermissions() {
  // Get the centralized permission store instance
  const permissionStore = usePermissionStore();
  const userStore = useUserStore();

  // Fallback computed properties for backward compatibility and reliability
  const safeUserRoles = computed(() => permissionStore.userRoles || userStore.getUserRoles);
  const safeUserIsReadOnly = computed(() => {
    const storeValue = permissionStore.userIsReadOnly;
    return storeValue !== undefined ? storeValue : userStore.isUserReadOnly;
  });
  const safeUserIsAdmin = computed(() => {
    const storeValue = permissionStore.userIsAdmin;
    return storeValue !== undefined ? storeValue : userStore.isUserAdmin;
  });

  return {
    // ===== Reactive State & Computed Properties =====
    
    /** User's current roles (reactive) */
    userRoles: permissionStore.userRoles,
    
    /** All user permissions computed from roles (reactive) */
    allUserPermissions: permissionStore.allUserPermissions,
    
    /** Whether user has admin privileges (reactive) */
    userIsAdmin: permissionStore.userIsAdmin,
    
    /** Whether user is in read-only mode (reactive) */
    userIsReadOnly: permissionStore.userIsReadOnly,

    // ===== Core Permission Checking Functions =====
    
    /** Check if user has a specific permission */
    can: permissionStore.can,
    
    /** Check if user has any of the specified permissions */
    canAny: permissionStore.canAny,
    
    /** Check if user has all of the specified permissions */
    canAll: permissionStore.canAll,
    
    /** Check if user can read from a specific module */
    canRead: permissionStore.canRead,
    
    /** Check if user can write to a specific module */
    canWrite: permissionStore.canWrite,
    
    /** Check if user can delete from a specific module */
    canDelete: permissionStore.canDelete,

    // ===== Dynamic Module Factory =====
    
    /** 
     * Create a reactive permission object for a specific module
     * @example
     * const calculator = module('CALCULATOR');
     * // calculator.canRead.value, calculator.canWrite.value, etc.
     */
    module: permissionStore.module,

    // ===== Module Permission Helpers =====
    
    /** Get all permission states for a specific module */
    getModulePermissions: permissionStore.getModulePermissions,
    
    /** Get permission states for all modules (debug/admin use) */
    getAllModulePermissions: permissionStore.getAllModulePermissions,

    // ===== Performance & Cache Management =====
    
    /** Clear all permission caches and force recalculation */
    clearCache: permissionStore.clearCache,
    
    /** Enable or disable permission caching */
    setCacheEnabled: permissionStore.setCacheEnabled,
    
    /** Get current cache statistics */
    getCacheStats: permissionStore.getCacheStats,

    // ===== Development & Debug Tools =====
    
    /** Output comprehensive permission debug information to console */
    debugPermissions: permissionStore.debugPermissions,
    
    /** Analyze which roles grant a specific permission */
    analyzePermission: permissionStore.analyzePermission,

    // ===== Legacy Compatibility =====

    /** @deprecated Use allUserPermissions instead */
    getAllPermissions: () => permissionStore.allUserPermissions,

    // ===== Safe Fallback Properties =====

    /** Safe user roles with fallback to user store */
    safeUserRoles,

    /** Safe read-only check with fallback to user store */
    safeUserIsReadOnly,

    /** Safe admin check with fallback to user store */
    safeUserIsAdmin,
  };
}